// The card at the top of a horse's page: what's happening to this horse.
//
// Everything else up there is reference data — breed, height, policy number —
// which is true for months at a time. This is the part that changed since you
// last looked, and it goes first because it's why somebody opened the page.
//
// It answers the same three questions as the card on the Horses list, from the
// same endpoint and the same rules, but with room to answer them properly: the
// full date rather than a weekday, how far away that is, and who the provider
// was. Where the list card omits an empty row to stay quiet, this one says
// "Nothing scheduled" out loud — you came here about this horse specifically,
// and knowing there is nothing is an answer.
//
// It deliberately doesn't repeat the health record list, the notes or the
// providers; all three have their own cards further down. This is the summary
// that points at them.

import {
  label,
  dayAndDate,
  longDate,
  relative,
  overdueFor,
  isAwayNow,
  nextAppointment,
  dueSoon,
} from '../../lib/horseActivity';

/**
 * One column. The color rule on top is the same system the list card uses:
 * green for something planned, amber for maintenance, gray for history.
 */
function Column({ tone, tag, headline, lines = [], empty }) {
  return (
    <div className="flex flex-col gap-1 min-w-0">
      <span className={'h-1 w-10 rounded-full ' + tone} aria-hidden="true" />
      <span className="text-[10px] font-medium tracking-wider text-ink-400 uppercase mt-1">
        {tag}
      </span>
      {headline ? (
        <>
          <span className="font-medium text-ink-900 leading-snug">{headline}</span>
          {lines.filter(Boolean).map((l, i) => (
            <span key={i} className="text-sm text-ink-500 leading-snug">
              {l}
            </span>
          ))}
        </>
      ) : (
        <span className="text-sm text-ink-400">{empty}</span>
      )}
    </div>
  );
}

export default function HorseSummary({ snapshot, today }) {
  // No payload yet, or a horse the endpoint returned nothing for. Rendering a
  // card of three "nothing" columns while the request is still in flight would
  // be a wrong answer shown confidently.
  if (!snapshot || !today) return null;

  const { lastService, overdue, event } = snapshot;
  const away = isAwayNow(snapshot, today);
  const next = nextAppointment(snapshot, today);
  const due = dueSoon(snapshot, today);

  return (
    <section className="card p-4 mb-4">
      {(overdue || away) && (
        <div className="flex flex-wrap items-center gap-1.5 mb-4">
          {overdue && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 px-2.5 py-1 text-xs font-medium leading-none">
              {label(overdue.type)} overdue
              <span className="tabular-nums font-normal">
                {overdueFor(today, overdue.date)}
              </span>
            </span>
          )}
          {away && (
            <span className="inline-flex items-center rounded-full bg-sky-50 text-sky-800 border border-sky-200 px-2.5 py-1 text-xs font-medium leading-none">
              At {event.title} · through {longDate(event.endDate)}
            </span>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
        {/* The date spelled out, because this is the headline of the card and
            a barn reads it as a sentence. Under it the distance — "in 2 days",
            "today" — which is the one thing the date itself doesn't tell you,
            and the only line that can't repeat what's above it.

            The other two columns lead with the kind of service instead, so
            their dates stay abbreviated: they're supporting a headline rather
            than being one. */}
        <Column
          tone="bg-hunter"
          tag="Next up"
          headline={
            next && (
              // A spaced colon rather than a middot: the date and the time are
              // two separate facts here, and the gap is doing the same work the
              // punctuation is.
              <span className="inline-flex items-baseline gap-2.5 flex-wrap">
                <span>{dayAndDate(next.date)}</span>
                {next.time && (
                  <>
                    <span className="text-ink-300 font-normal">:</span>
                    <span>{next.time}</span>
                  </>
                )}
              </span>
            )
          }
          lines={next ? [next.detail, relative(today, next.date)] : []}
          empty="Nothing scheduled"
        />

        <Column
          tone="bg-amber-500"
          tag="Due next"
          headline={due && label(due.type)}
          lines={due ? [longDate(due.date), relative(today, due.date)] : []}
          empty={overdue ? 'Nothing else due' : 'Nothing due'}
        />

        <Column
          tone="bg-ink-300"
          tag="Last service"
          headline={lastService && label(lastService.type)}
          lines={
            lastService
              ? [
                  longDate(lastService.date),
                  [relative(today, lastService.date), lastService.providerName]
                    .filter(Boolean)
                    .join(' · '),
                ]
              : []
          }
          empty="Nothing recorded"
        />
      </div>
    </section>
  );
}
